/**
 * useUrlFilterState.js — tiny hook that backs a filter control with the URL.
 *
 * Same signature as `useState` (`[value, setValue]`) but every change is
 * reflected in `location.search` as a query param, using `replace` so the
 * browser back button does not collect a history entry per keystroke.
 *
 * Usage:
 *   const [q, setQ] = useUrlFilterState('q', '');
 *   const [eta, setEta] = useUrlFilterState('eta', '');
 *   const clearFilters = useUrlFilterClear(['q', 'eta', 'supplier']);
 *
 * When the param equals the `defaultValue`, it is removed from the URL to
 * keep shared links clean.
 */
import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

export default function useUrlFilterState(key, defaultValue = '') {
  const [params, setParams] = useSearchParams();
  const raw = params.get(key);
  const value = raw === null ? defaultValue : raw;

  const setValue = useCallback((next) => {
    setParams((prev) => {
      const copy = new URLSearchParams(prev);
      const resolved = typeof next === 'function' ? next(copy.get(key) ?? defaultValue) : next;
      if (resolved === '' || resolved === null || resolved === undefined || resolved === defaultValue) {
        copy.delete(key);
      } else {
        copy.set(key, String(resolved));
      }
      return copy;
    }, { replace: true });
  }, [setParams, key, defaultValue]);

  return [value, setValue];
}

/**
 * Returns a function that atomically clears every listed param key in ONE
 * URL write. Necessary because calling several `setValue` setters in
 * sequence can race against React batching and drop some of the updates.
 */
export function useUrlFilterClear(keys) {
  const [, setParams] = useSearchParams();
  return useCallback(() => {
    setParams((prev) => {
      const copy = new URLSearchParams(prev);
      keys.forEach((k) => copy.delete(k));
      return copy;
    }, { replace: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setParams, JSON.stringify(keys)]);
}
